import PageHero from "../components/sections/PageHero";
import CTABlock from "../components/sections/CTABlock";

const expertiseList = [
  "Corporate Advisory",
  "Commercial Contracts",
  "Dispute Resolution",
  "Civil Litigation",
  "Criminal Litigation",
  "RERA Litigation",
  "Real Estate Advisory",
  "Legal Notices",
  "Pleadings",
  "Agreements",
  "Due Diligence",
  "Strategic Legal Documentation",
];

const associations = [
  "Member, Bar Council of Delhi",
  "Member, Delhi High Court Bar Association",
  "Member, Gurugram Bar Association",
];

const courts = [
  "District Courts",
  "High Courts",
  "RERA Authorities",
  "Tribunals",
  "Other Adjudicatory Forums",
  "Matters capable of escalation before the Supreme Court of India",
];

export default function PrincipalAdvocatePage() {
  return (
    <>
      <PageHero
        eyebrow="Principal Advocate"
        title="Varun Singh Hooda"
        subtitle="Principal Advocate, Lex Polaris, Advocates & Solicitors."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Principal Advocate" }]}
      />

      {/* PROFILE */}
      <section className="bg-ivory section-y" data-testid="principal-profile">
        <div className="container-px max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden bg-navy/10">
              <img
                src="/brand/varun-singh-hooda.jpeg"
                alt="Varun Singh Hooda, Principal Advocate at Lex Polaris"
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
            </div>
            <div className="mt-6 grid grid-cols-2 gap-px bg-soft-grey border border-soft-grey">
              <div className="bg-white p-5">
                <div className="text-xs tracking-[0.22em] uppercase text-charcoal/55">Experience</div>
                <div className="font-serif text-3xl text-navy mt-2">12<span className="text-gold">+</span></div>
                <div className="text-xs text-charcoal/60 mt-1">Years of Active Practice</div>
              </div>
              <div className="bg-white p-5">
                <div className="text-xs tracking-[0.22em] uppercase text-charcoal/55">Based</div>
                <div className="font-serif text-2xl text-navy mt-2 leading-tight">Delhi NCR<br />&amp; Gurugram</div>
              </div>
            </div>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <div className="overline mb-6">Professional Profile</div>
            <h2 className="h-display text-3xl md:text-4xl lg:text-5xl text-navy mb-7">
              Strategic counsel across litigation, advisory and dispute resolution.
            </h2>
            <div className="editorial-rule mb-7" />
            <p className="body-lg text-charcoal/80 mb-6">
              Varun Singh Hooda is Principal Advocate at Lex Polaris, Advocates &amp; Solicitors, with 12+ years
              of active experience in civil, criminal and commercial litigation, real estate disputes, RERA
              proceedings, corporate advisory, legal drafting, contract structuring, dispute strategy and client
              representation before courts and tribunals.
            </p>
            <p className="body-lg text-charcoal/70">
              His practice focuses on legally sound, commercially practical and outcome-oriented solutions for
              individuals, businesses, start-ups, real estate stakeholders and corporate clients.
            </p>
          </div>
        </div>
      </section>

      {/* CORE EXPERTISE */}
      <section className="bg-white border-y border-soft-grey section-y" data-testid="principal-expertise">
        <div className="container-px max-w-[1400px] mx-auto">
          <div className="max-w-3xl mb-12 md:mb-16">
            <div className="overline mb-6">Core Expertise</div>
            <h2 className="h-display text-4xl md:text-5xl text-navy">
              Twelve focused areas of legal practice.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-soft-grey border border-soft-grey">
            {expertiseList.map((e, i) => (
              <div
                key={e}
                data-testid={`principal-expertise-${i}`}
                className="bg-white p-6 md:p-7 flex items-start gap-4"
              >
                <span className="font-serif text-gold text-xs pt-1.5">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-serif text-lg text-navy leading-snug">{e}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BAR ENROLMENT */}
      <section className="bg-navy text-ivory section-y" data-testid="principal-bar">
        <div className="container-px max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline text-gold mb-6">Bar Enrolment &amp; Associations</div>
            <h2 className="h-display text-4xl md:text-5xl text-ivory">
              Active member of leading bar associations.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <ul className="divide-y divide-ivory/15 border-t border-b border-ivory/15">
              {associations.map((a, i) => (
                <li
                  key={a}
                  data-testid={`principal-association-${i}`}
                  className="py-6 flex items-start gap-5"
                >
                  <span className="font-serif text-gold text-sm pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif text-2xl md:text-3xl text-ivory leading-snug">{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* COURTS */}
      <section className="bg-ivory section-y" data-testid="principal-courts">
        <div className="container-px max-w-[1400px] mx-auto">
          <div className="max-w-3xl mb-12 md:mb-16">
            <div className="overline mb-6">Courts &amp; Forums</div>
            <h2 className="h-display text-4xl md:text-5xl text-navy">
              Representation across courts, authorities and forums.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-soft-grey border border-soft-grey">
            {courts.map((c, i) => (
              <div
                key={c}
                data-testid={`principal-court-${i}`}
                className="bg-white p-7 md:p-9"
              >
                <div className="font-serif text-gold text-xs mb-5">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-serif text-xl md:text-2xl text-navy leading-snug">{c}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABlock
        eyebrow="Consultation"
        heading="Discuss your matter with Lex Polaris."
        body="A focused consultation can clarify your legal position, available remedies and the most practical next step."
        primaryLabel="Book a Consultation"
        secondaryLabel="WhatsApp Us"
        secondaryHref="https://wa.me/919810899124?text=Hello%20Lex%20Polaris%20Team%2C%20I%20would%20like%20to%20discuss%20a%20legal%20matter."
      />
    </>
  );
}
